/**
 * FbeAccountabilityPanel — QST-345 / FBE-5 (LOG-ONLY).
 *
 * Renders session-scoped accountability for the Forecast Belief Engine using
 * the ``fbeForecastOutcomes`` table populated by QST-344
 * (``scripts/fbe_outcome_tracker.py``). This is **watch-only** — the panel
 * never influences trade execution, alerts, sizing, or signal gates. The
 * "FBE LOG-ONLY · Not used for execution" disclaimer is mandatory and lives at
 * the top of every render path (loading, error, empty, success) via the
 * shared ``PanelShell`` layout.
 *
 * Two views share the same row payload:
 *
 *   1. Current-session live state per symbol — latest forecast per symbol
 *      with each horizon's pending/win/loss/flat result.
 *   2. Calibration buckets — completed rows grouped by
 *      ``(symbol, session, expected_direction, confidence_bucket, horizon)``
 *      with follow-through %, average ticks, sample size, and Brier score.
 *
 * Data fetching contract (QST-345 review FIX FIRST blocker 1):
 *   The Convex ``getFbeForecastOutcomes`` query is capped at 500 rows. If we
 *   queried globally and filtered client-side on session/symbol, a noisy
 *   session/symbol could drown out the selected one — the selected slice
 *   would render as empty even though completed rows exist in the table.
 *   To avoid that, the panel is split into a presentational
 *   ``FbeAccountabilityPanel`` (props-driven, easy to unit-test with stubbed
 *   ``state``) and a ``FbeAccountabilityPanelContainer`` that owns
 *   ``sessionTab`` + ``symbolFilter`` and re-keys the Convex query whenever
 *   those filters change. App.tsx renders the container; the legacy panel
 *   is only used by tests with a synthetic ``state`` prop.
 *
 * Design rules (per ticket):
 *   * Trading-cockpit dense but readable; no purple/AI gradient.
 *   * Neutral / amber LOG-ONLY styling.
 *   * Green/red ONLY for directional outcomes (win/loss).
 *   * Missing outcome rows render an empty/pending state — never crash.
 */
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { api } from "../convex-api/api";
import { useQueryWithState, type QueryState } from "../hooks/useQueryWithState";
import { DASHBOARD_SYMBOLS } from "../lib/dashboardDataContract";
import { defaultDashboardSession } from "../lib/sessionClock";
import {
  buildFbeCardGuidance,
  buildFbeForecastView,
  isScoreableFbeSessionForecast,
  type FbeForecastFields,
} from "../lib/fbeForecastView";
import { EmptyState, ErrorState, LoadingState } from "./states";

/* ── Row contract — mirrors convex/schema.ts fbeForecastOutcomes ── */

export interface FbeForecastMeta extends FbeForecastFields {
  symbol: string;
  session: string;               // "asia" | "us"
  session_label?: string;        // "Asia" | "NY/US"
  date?: string;
  forecast_created_at: number;
  forecast_created_at_iso?: string;
  created_at?: number;
  updated_at?: number;
}

export interface FbeOutcomeRow extends FbeForecastMeta {
  _id?: string;
  date: string;
  forecast_scope: string;        // "session"
  session_start_ms?: number | null;
  session_end_ms?: number | null;
  horizon_label: string;         // "5m" | "15m" | "30m" | "60m" | "session_close"
  horizon_min?: number | null;
  horizon_due_ms: number;
  status: string;                // "pending" | "completed"
  price_at_forecast: number;
  price_at_horizon?: number | null;
  completed_at?: number | null;
  expected_direction: string;    // "up" | "down" | "flat"
  directional_result: string;    // "pending" | "win" | "loss" | "flat"
  move_points?: number | null;
  move_ticks?: number | null;
  brier_score?: number | null;
  forecast_raw_state: string;
  forecast_raw_confidence: number;
  forecast_raw_probs?: unknown;
  forecast_stable_state: string;
  forecast_stable_confidence: number;
  forecast_transition_probability: number;
  forecast_state_age_min: number;
  forecast_flip_suppressed: boolean;
  forecast_stability_score: number;
  forecast_reason?: string;
  forecast_engine_version?: string;
  forecast_trader_bias?: string;
  forecast_trade_mode?: string;
  forecast_preferred_setups?: string[] | unknown;
  forecast_avoid_setups?: string[] | unknown;
  forecast_guidance_reason?: string;
  forecast_guidance_contract?: string;
  forecast_guidance_execution_approved?: boolean;
  forecast_contract?: string;
  forecast_anchor_type?: string;
  forecast_session_id?: string;
  layer_snapshot?: unknown;
  layer_votes?: unknown;
  layer_freshness?: unknown;
  layer_attribution_version?: string;
}

export type FbeExpectedDirection = "up" | "down" | "flat";

function isScoreableSessionForecastRow(row: FbeOutcomeRow): boolean {
  return isScoreableFbeSessionForecast(row);
}

interface FbeAccountabilityPanelProps {
  state: QueryState<FbeOutcomeRow[]>;
  /** Latest liveMarketSnapshots by symbol for the selected FBE session. */
  liveForecasts?: Record<string, unknown> | null;
  /**
   * Controlled session tab. When provided together with
   * ``onSessionTab``, the panel renders in controlled mode (used by
   * ``FbeAccountabilityPanelContainer`` which lifts the state so it can
   * re-key the scoped Convex query). Omit both to fall back to internal
   * ``useState`` — the legacy test-friendly mode.
   */
  sessionTab?: "asia" | "us";
  onSessionTab?: (s: "asia" | "us") => void;
  /** Controlled symbol filter; same controlled/uncontrolled contract. */
  symbolFilter?: "all" | string;
  onSymbolFilter?: (s: "all" | string) => void;
}

const SESSION_TABS: readonly { key: "asia" | "us"; label: string }[] = [
  { key: "asia", label: "Asia" },
  { key: "us", label: "NY/US" },
];

const HORIZON_ORDER: readonly string[] = ["5m", "15m", "30m", "60m", "session_close"];
const HORIZON_DEFAULT_FILTER = "15m"; // ticket calls out 15m as the headline horizon
const MIN_COMPLETED_FOR_EDGE_LABEL = 50;
const MIN_DECIDED_FOR_EDGE_LABEL = 30;
const WATCH_DECIDED_WIN_RATE = 0.55;
const WEAK_DECIDED_WIN_RATE = 0.45;
const GOOD_BRIER_MAX = 0.70;
const WEAK_BRIER_MIN = 0.85;
const PROMOTION_MIN_ACTIONABLE_COMPLETED = 100;
const PROMOTION_MIN_DECIDED = 60;
const PROMOTION_MIN_DECIDED_WIN_RATE = 0.58;
const PROMOTION_MAX_BRIER = 0.45;

const STATE_COLORS: Record<string, string> = {
  bullish: "var(--green, #22c55e)",
  bearish: "var(--red, #ef4444)",
  flat: "var(--text-dim, #a0a0b8)",
  mixed: "var(--yellow, #eab308)",
  unstable: "#f97316",
};

const RESULT_COLORS: Record<string, string> = {
  win: "var(--green, #22c55e)",
  loss: "var(--red, #ef4444)",
  flat: "var(--text-dim, #a0a0b8)",
  pending: "var(--text-dimmer, #6b7280)",
};

/* ── Pure helpers (exported for unit tests) ── */

/**
 * Map a 0–100 stable-confidence value to a calibration bucket label.
 * Returns ``null`` for low-confidence (<50%) rows that shouldn't be
 * counted in calibration buckets — those are too weak to grade.
 */
export function confidenceBucketLabel(confidence: number | null | undefined): string | null {
  if (typeof confidence !== "number" || !Number.isFinite(confidence)) return null;
  const c = confidence;
  if (c < 50) return null;
  if (c < 60) return "50–60%";
  if (c < 70) return "60–70%";
  if (c < 80) return "70–80%";
  if (c < 90) return "80–90%";
  return "90–100%";
}

/**
 * Human-readable session label for a session token.
 * Always returns a string so the panel renders even on unexpected sessions.
 */
export function sessionLabel(session: string, fallback?: string | null): string {
  if (fallback) return fallback;
  const s = (session || "").toLowerCase();
  if (s === "asia") return "Asia";
  if (s === "us") return "NY/US";
  return session?.toUpperCase() || "—";
}

export function sessionForecastLabel(session: "asia" | "us"): string {
  return `${sessionLabel(session)} Session Forecast`;
}

/** Pretty-print a forecast state token. */
export function stateLabel(state: string | undefined | null): string {
  return (state || "—").toUpperCase();
}

/** Pretty-print a horizon token. ``session_close`` becomes ``Close``. */
export function horizonShort(horizon: string): string {
  return horizon === "session_close" ? "Close" : horizon;
}

function formatPct(value: number | null, digits = 0): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

function formatTicks(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : value < 0 ? "" : "";
  return `${sign}${value.toFixed(1)}t`;
}

function formatBrier(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return value.toFixed(2);
}

function formatConfidence(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(0)}%`;
}

function horizonRank(horizon: string): number {
  const i = HORIZON_ORDER.indexOf(horizon);
  return i === -1 ? HORIZON_ORDER.length : i;
}

function comparePromotionReviewRows(a: FbePromotionReviewRow, b: FbePromotionReviewRow): number {
  return (
    PROMOTION_READINESS_RANK[a.readiness.status] - PROMOTION_READINESS_RANK[b.readiness.status] ||
    b.actionableCompleted - a.actionableCompleted ||
    (b.decidedWinRate ?? -1) - (a.decidedWinRate ?? -1) ||
    a.symbol.localeCompare(b.symbol) ||
    a.session.localeCompare(b.session) ||
    horizonRank(a.horizon) - horizonRank(b.horizon)
  );
}

/** Capitalised expected-direction label. */
function expectedLabel(direction: string): string {
  const d = (direction || "").toLowerCase();
  if (d === "up") return "Bullish";
  if (d === "down") return "Bearish";
  return "Flat";
}

function expectedDirectionKey(direction: string): FbeExpectedDirection | null {
  const d = (direction || "").toLowerCase();
  if (d === "up" || d === "down" || d === "flat") return d;
  return null;
}

/* ── Aggregations ── */

export interface CalibrationBucket {
  symbol: string;
  session: string;
  expectedDirection: string;
  confidenceBucket: string;
  horizon: string;
  n: number;                // total rows in this bucket (pending + completed)
  nCompleted: number;
  nWins: number;
  nLosses: number;
  nFlats: number;
  followThrough: number | null;  // wins/completed in [0,1]
  decidedWinRate: number | null; // wins/(wins+losses), ignoring flat outcomes
  avgTicks: number | null;
  avgBrier: number | null;
}

export type CalibrationEvidenceLabel = "WATCH" | "COLLECT_MORE" | "WEAK/AVOID" | "INSUFFICIENT_SAMPLE" | "NO_ACTIONABLE_FORECASTS";

export interface FbeHorizonSummary {
  horizon: string;
  decided: number;
  wins: number;
  losses: number;
  decidedWinRate: number;
}

export interface FbeSessionSummary {
  total: number;
  pending: number;
  completed: number;
  wins: number;
  losses: number;
  flats: number;
  actionableCompleted: number;
  decided: number;
  decidedWinRate: number | null;
  flatRate: number | null;
  avgBrier: number | null;
  evidenceLabel: CalibrationEvidenceLabel;
  bestHorizon: FbeHorizonSummary | null;
  worstHorizon: FbeHorizonSummary | null;
}

export type FbePromotionReadinessStatus = "collecting_sample" | "not_ready" | "manual_review_candidate";
export type FbePromotionReviewFilter = "all" | FbePromotionReadinessStatus;
export type FbePromotionReviewSort = "readiness" | "sample" | "win_rate" | "brier";

export interface FbePromotionReadiness {
  status: FbePromotionReadinessStatus;
  label: "COLLECTING SAMPLE" | "NOT PROMOTION READY" | "MANUAL REVIEW CANDIDATE";
  reasons: string[];
}

export interface FbePromotionReviewRow {
  symbol: string;
  session: string;
  horizon: string;
  actionableCompleted: number;
  decided: number;
  decidedWinRate: number | null;
  avgBrier: number | null;
  readiness: FbePromotionReadiness;
}

export interface FbePromotionReviewOutcomeSummary {
  id: string;
  result: string;
  expectedDirection: string;
  stableConfidence: number;
  moveTicks: number | null;
  brierScore: number | null;
  completedAt: number | null;
}

export interface FbePromotionReviewEvidence {
  total: number;
  pending: number;
  completed: number;
  wins: number;
  losses: number;
  flats: number;
  avgStableConfidence: number | null;
  worstMisses: FbePromotionReviewOutcomeSummary[];
  recentOutcomes: FbePromotionReviewOutcomeSummary[];
}

export interface FbeLayerAttributionRow {
  symbol: string;
  forecastState: string;
  version: string;
  topVotes: Array<{ layer: string; direction: string; contribution: number | null }>;
  freshnessLine: string;
}

const PROMOTION_READINESS_RANK: Record<FbePromotionReadinessStatus, number> = {
  manual_review_candidate: 0,
  not_ready: 1,
  collecting_sample: 2,
};

export interface FbeDirectionSummary {
  expectedDirection: FbeExpectedDirection;
  total: number;
  pending: number;
  completed: number;
  wins: number;
  losses: number;
  flats: number;
  decided: number;
  decidedWinRate: number | null;
  avgBrier: number | null;
}

export interface FbeHorizonBreakdownSummary {
  horizon: string;
  total: number;
  pending: number;
  completed: number;
  wins: number;
  losses: number;
  flats: number;
  decided: number;
  decidedWinRate: number | null;
  avgBrier: number | null;
}

export type FbeScoreboardVerdict = "HELPFUL" | "NOISY" | "HARMFUL" | "COLLECTING";
export type FbeAccuracyRecommendationAction = "candidate_weight_up" | "candidate_weight_down_or_no_edge" | "collect_more_outcomes" | "keep_log_only_no_change";

export interface FbeScoreboardRow {
  symbol: string;
  session: string;
  expectedDirection: FbeExpectedDirection;
  total: number;
  pending: number;
  completed: number;
  actionableCompleted: number;
  decided: number;
  wins: number;
  losses: number;
  flats: number;
  decidedWinRate: number | null;
  avgBrier: number | null;
  avgMoveTicks: number | null;
  verdict: FbeScoreboardVerdict;
}

export interface FbeAccuracyRecommendationRow {
  surface: "accountability_only";
  symbol: string;
  session: string;
  expectedDirection: FbeExpectedDirection;
  action: FbeAccuracyRecommendationAction;
  priority: "high" | "medium" | "low";
  rationale: string;
  decided: number;
  decidedWinRate: number | null;
  avgBrier: number | null;
  avgMoveTicks: number | null;
}

function evidenceLabelFromMetrics(
  actionableCompleted: number,
  decided: number,
  decidedWinRate: number | null,
  avgBrier: number | null,
): CalibrationEvidenceLabel {
  if (actionableCompleted === 0) return "NO_ACTIONABLE_FORECASTS";
  if (actionableCompleted < MIN_COMPLETED_FOR_EDGE_LABEL || decided < MIN_DECIDED_FOR_EDGE_LABEL) {
    return "INSUFFICIENT_SAMPLE";
  }
  if (decidedWinRate === null) return "INSUFFICIENT_SAMPLE";
  if (decidedWinRate >= WATCH_DECIDED_WIN_RATE && (avgBrier === null || avgBrier <= GOOD_BRIER_MAX)) {
    return "WATCH";
  }
  if (decidedWinRate <= WEAK_DECIDED_WIN_RATE || (avgBrier !== null && avgBrier >= WEAK_BRIER_MIN)) {
    return "WEAK/AVOID";
  }
  return "COLLECT_MORE";
}

/**
 * Aggregate completed outcome rows into calibration buckets.
 *
 * Buckets key on ``(symbol, session, expected_direction, confidence_bucket,
 * horizon)``. Rows that fall under 50% confidence are excluded — those are
 * intentionally not graded for calibration accuracy.
 *
 * Pure function so the tests can pin its behaviour without React.
 */
export function buildCalibrationBuckets(rows: FbeOutcomeRow[]): CalibrationBucket[] {
  type Acc = {
    symbol: string;
    session: string;
    expectedDirection: string;
    confidenceBucket: string;
    horizon: string;
    n: number;
    nCompleted: number;
    nWins: number;
    nLosses: number;
    nFlats: number;
    sumTicks: number;
    sumBrier: number;
    nTicks: number;
    nBrier: number;
  };
  const map = new Map<string, Acc>();

  for (const row of rows) {
    const bucket = confidenceBucketLabel(row.forecast_stable_confidence);
    if (!bucket) continue;
    const key = `${row.symbol}|${row.session}|${row.expected_direction}|${bucket}|${row.horizon_label}`;
    let acc = map.get(key);
    if (!acc) {
      acc = {
        symbol: row.symbol,
        session: row.session,
        expectedDirection: row.expected_direction,
        confidenceBucket: bucket,
        horizon: row.horizon_label,
        n: 0,
        nCompleted: 0,
        nWins: 0,
        nLosses: 0,
        nFlats: 0,
        sumTicks: 0,
        sumBrier: 0,
        nTicks: 0,
        nBrier: 0,
      };
      map.set(key, acc);
    }
    acc.n += 1;
    if (row.status === "completed") {
      acc.nCompleted += 1;
      if (row.directional_result === "win") acc.nWins += 1;
      if (row.directional_result === "loss") acc.nLosses += 1;
      if (row.directional_result === "flat") acc.nFlats += 1;
      if (typeof row.move_ticks === "number" && Number.isFinite(row.move_ticks)) {
        acc.sumTicks += row.move_ticks;
        acc.nTicks += 1;
      }
      if (typeof row.brier_score === "number" && Number.isFinite(row.brier_score)) {
        acc.sumBrier += row.brier_score;
        acc.nBrier += 1;
      }
    }
  }

  const result: CalibrationBucket[] = [];
  for (const acc of map.values()) {
    result.push({
      symbol: acc.symbol,
      session: acc.session,
      expectedDirection: acc.expectedDirection,
      confidenceBucket: acc.confidenceBucket,
      horizon: acc.horizon,
      n: acc.n,
      nCompleted: acc.nCompleted,
      nWins: acc.nWins,
      nLosses: acc.nLosses,
      nFlats: acc.nFlats,
      followThrough: acc.nCompleted > 0 ? acc.nWins / acc.nCompleted : null,
      decidedWinRate: acc.nWins + acc.nLosses > 0 ? acc.nWins / (acc.nWins + acc.nLosses) : null,
      avgTicks: acc.nTicks > 0 ? acc.sumTicks / acc.nTicks : null,
      avgBrier: acc.nBrier > 0 ? acc.sumBrier / acc.nBrier : null,
    });
  }

  // Sort: symbol, expected (up>down>flat), bucket (low→high), horizon order.
  const dirOrder = (d: string) => (d === "up" ? 0 : d === "down" ? 1 : 2);
  const horizonIdx = (h: string) => {
    const i = HORIZON_ORDER.indexOf(h);
    return i === -1 ? HORIZON_ORDER.length : i;
  };
  result.sort((a, b) =>
    a.symbol.localeCompare(b.symbol) ||
    dirOrder(a.expectedDirection) - dirOrder(b.expectedDirection) ||
    a.confidenceBucket.localeCompare(b.confidenceBucket) ||
    horizonIdx(a.horizon) - horizonIdx(b.horizon));
  return result;
}

/** Conservative LOG-ONLY label for dashboard calibration buckets. */
export function calibrationEvidenceLabel(bucket: CalibrationBucket): CalibrationEvidenceLabel {
  if (bucket.expectedDirection === "flat" || bucket.nCompleted === 0) {
    return "NO_ACTIONABLE_FORECASTS";
  }
  const decided = bucket.nWins + bucket.nLosses;
  return evidenceLabelFromMetrics(bucket.nCompleted, decided, bucket.decidedWinRate, bucket.avgBrier);
}

/** Session-level FBE summary used by the dashboard headline card. */
export function buildFbeSummary(rows: FbeOutcomeRow[]): FbeSessionSummary {
  type HorizonAcc = { horizon: string; wins: number; losses: number };
  const horizonMap = new Map<string, HorizonAcc>();

  let pending = 0;
  let completed = 0;
  let wins = 0;
  let losses = 0;
  let flats = 0;
  let actionableWins = 0;
  let actionableLosses = 0;
  let actionableCompleted = 0;
  let sumBrier = 0;
  let nBrier = 0;

  for (const row of rows) {
    if (row.status === "pending") pending += 1;
    if (row.status !== "completed") continue;

    completed += 1;
    const actionable = row.expected_direction !== "flat" && confidenceBucketLabel(row.forecast_stable_confidence) !== null;
    if (actionable) actionableCompleted += 1;
    if (actionable && typeof row.brier_score === "number" && Number.isFinite(row.brier_score)) {
      sumBrier += row.brier_score;
      nBrier += 1;
    }

    if (row.directional_result === "win") wins += 1;
    else if (row.directional_result === "loss") losses += 1;
    else if (row.directional_result === "flat") flats += 1;

    if (actionable && (row.directional_result === "win" || row.directional_result === "loss")) {
      if (row.directional_result === "win") actionableWins += 1;
      else actionableLosses += 1;
      let acc = horizonMap.get(row.horizon_label);
      if (!acc) {
        acc = { horizon: row.horizon_label, wins: 0, losses: 0 };
        horizonMap.set(row.horizon_label, acc);
      }
      if (row.directional_result === "win") acc.wins += 1;
      else acc.losses += 1;
    }
  }

  const decided = actionableWins + actionableLosses;
  const decidedWinRate = decided > 0 ? actionableWins / decided : null;
  const avgBrier = nBrier > 0 ? sumBrier / nBrier : null;
  const horizons: FbeHorizonSummary[] = Array.from(horizonMap.values())
    .map((h) => ({
      horizon: h.horizon,
      wins: h.wins,
      losses: h.losses,
      decided: h.wins + h.losses,
      decidedWinRate: (h.wins + h.losses) > 0 ? h.wins / (h.wins + h.losses) : 0,
    }))
    .filter((h) => h.decided > 0);
  const horizonIdx = (h: string) => {
    const i = HORIZON_ORDER.indexOf(h);
    return i === -1 ? HORIZON_ORDER.length : i;
  };
  const bestHorizon = horizons.length > 0
    ? ([...horizons].sort((a, b) => b.decidedWinRate - a.decidedWinRate || b.decided - a.decided || horizonIdx(a.horizon) - horizonIdx(b.horizon))[0] ?? null)
    : null;
  const worstHorizon = horizons.length > 0
    ? ([...horizons].sort((a, b) => a.decidedWinRate - b.decidedWinRate || b.decided - a.decided || horizonIdx(a.horizon) - horizonIdx(b.horizon))[0] ?? null)
    : null;

  return {
    total: rows.length,
    pending,
    completed,
    wins,
    losses,
    flats,
    actionableCompleted,
    decided,
    decidedWinRate,
    flatRate: completed > 0 ? flats / completed : null,
    avgBrier,
    evidenceLabel: evidenceLabelFromMetrics(actionableCompleted, decided, decidedWinRate, avgBrier),
    bestHorizon,
    worstHorizon,
  };
}

/**
 * CTO promotion gate for FBE accountability.
 *
 * This is deliberately stricter than the bucket evidence label and never
 * returns "approved". Passing these gates only means the forecast lane is
 * worth manual review; FBE stays LOG-ONLY until Sabastian explicitly promotes
 * it through a separate trading-system change.
 */
export function buildFbePromotionReadiness(summary: FbeSessionSummary): FbePromotionReadiness {
  const sampleReasons: string[] = [];
  if (summary.actionableCompleted < PROMOTION_MIN_ACTIONABLE_COMPLETED) {
    sampleReasons.push(`${summary.actionableCompleted}/${PROMOTION_MIN_ACTIONABLE_COMPLETED} actionable completed`);
  }
  if (summary.decided < PROMOTION_MIN_DECIDED) {
    sampleReasons.push(`${summary.decided}/${PROMOTION_MIN_DECIDED} decided directional outcomes`);
  }
  if (sampleReasons.length > 0) {
    return {
      status: "collecting_sample",
      label: "COLLECTING SAMPLE",
      reasons: sampleReasons,
    };
  }

  const performanceReasons: string[] = [];
  if (summary.decidedWinRate === null || summary.decidedWinRate < PROMOTION_MIN_DECIDED_WIN_RATE) {
    performanceReasons.push(`win rate ${formatPct(summary.decidedWinRate)} below ${formatPct(PROMOTION_MIN_DECIDED_WIN_RATE)}`);
  }
  if (summary.avgBrier === null || summary.avgBrier > PROMOTION_MAX_BRIER) {
    performanceReasons.push(`Brier ${formatBrier(summary.avgBrier)} above ${formatBrier(PROMOTION_MAX_BRIER)}`);
  }
  if (performanceReasons.length > 0) {
    return {
      status: "not_ready",
      label: "NOT PROMOTION READY",
      reasons: performanceReasons,
    };
  }

  return {
    status: "manual_review_candidate",
    label: "MANUAL REVIEW CANDIDATE",
    reasons: [
      `${summary.actionableCompleted} actionable completed`,
      `${summary.decided} decided directional outcomes`,
      `${formatPct(summary.decidedWinRate)} decided WR`,
      `${formatBrier(summary.avgBrier)} avg Brier`,
      "LOG-ONLY until Sabastian explicitly promotes it",
    ],
  };
}

/**
 * CTO review table grouped at the smallest safe promotion boundary.
 *
 * Aggregate FBE performance can hide that one symbol/horizon has edge while
 * another is weak. This helper keeps the accountability view decision-ready
 * without changing execution: every row is still LOG-ONLY and the status is
 * at most a manual-review candidate.
 */
export function buildFbePromotionReviewRows(rows: FbeOutcomeRow[]): FbePromotionReviewRow[] {
  const groups = new Map<string, FbeOutcomeRow[]>();
  for (const row of rows) {
    if (!isScoreableSessionForecastRow(row)) continue;
    const key = `${row.symbol}|${row.session}|${row.horizon_label}`;
    const group = groups.get(key) ?? [];
    group.push(row);
    groups.set(key, group);
  }

  return [...groups.values()]
    .map((group) => {
      const [first] = group;
      const summary = buildFbeSummary(group);
      return {
        symbol: first?.symbol ?? "—",
        session: first?.session ?? "—",
        horizon: first?.horizon_label ?? "—",
        actionableCompleted: summary.actionableCompleted,
        decided: summary.decided,
        decidedWinRate: summary.decidedWinRate,
        avgBrier: summary.avgBrier,
        readiness: buildFbePromotionReadiness(summary),
      };
    })
    .sort(comparePromotionReviewRows);
}

function classifyFbeScoreboardRow(row: Omit<FbeScoreboardRow, "verdict">): FbeScoreboardVerdict {
  if (row.actionableCompleted < 30 || row.decided < 20) return "COLLECTING";
  if ((row.decidedWinRate ?? 0) <= 0.45 || (row.avgMoveTicks !== null && row.avgMoveTicks < 0) || (row.avgBrier !== null && row.avgBrier > 0.6)) {
    return "HARMFUL";
  }
  if ((row.decidedWinRate ?? 0) >= 0.56 && (row.avgBrier === null || row.avgBrier <= 0.45) && (row.avgMoveTicks === null || row.avgMoveTicks > 0)) {
    return "HELPFUL";
  }
  return "NOISY";
}

/**
 * CTO scoreboard: compact edge-quality buckets for manual FBE review.
 *
 * Grouping by symbol + session + forecast bias keeps the question honest:
 * is this forecast helpful for this market and session, or is it noise?
 * This remains read-only/LOG-ONLY and never feeds execution actionability.
 */
export function buildFbeScoreboardRows(rows: FbeOutcomeRow[]): FbeScoreboardRow[] {
  const groups = new Map<string, FbeOutcomeRow[]>();
  for (const row of rows) {
    if (!isScoreableSessionForecastRow(row)) continue;
    const direction = expectedDirectionKey(row.expected_direction) ?? "flat";
    const key = `${row.symbol}|${row.session}|${direction}`;
    const group = groups.get(key) ?? [];
    group.push(row);
    groups.set(key, group);
  }

  return [...groups.values()]
    .map((group) => {
      const [first] = group;
      let pending = 0;
      let completed = 0;
      let actionableCompleted = 0;
      let wins = 0;
      let losses = 0;
      let flats = 0;
      let moveSum = 0;
      let moveN = 0;
      let brierSum = 0;
      let brierN = 0;

      for (const row of group) {
        if (row.status === "pending") pending += 1;
        if (row.status !== "completed") continue;
        completed += 1;
        const actionable = row.expected_direction !== "flat" && confidenceBucketLabel(row.forecast_stable_confidence) !== null;
        if (actionable) actionableCompleted += 1;
        if (row.directional_result === "win") wins += 1;
        else if (row.directional_result === "loss") losses += 1;
        else if (row.directional_result === "flat") flats += 1;
        if (typeof row.move_ticks === "number" && Number.isFinite(row.move_ticks)) {
          moveSum += row.move_ticks;
          moveN += 1;
        }
        if (actionable && typeof row.brier_score === "number" && Number.isFinite(row.brier_score)) {
          brierSum += row.brier_score;
          brierN += 1;
        }
      }

      const decided = wins + losses;
      const base = {
        symbol: first?.symbol ?? "—",
        session: first?.session ?? "—",
        expectedDirection: expectedDirectionKey(first?.expected_direction ?? "") ?? "flat",
        total: group.length,
        pending,
        completed,
        actionableCompleted,
        decided,
        wins,
        losses,
        flats,
        decidedWinRate: decided > 0 ? wins / decided : null,
        avgBrier: brierN > 0 ? brierSum / brierN : null,
        avgMoveTicks: moveN > 0 ? moveSum / moveN : null,
      } satisfies Omit<FbeScoreboardRow, "verdict">;
      return { ...base, verdict: classifyFbeScoreboardRow(base) };
    })
    .sort((a, b) => {
      const verdictRank: Record<FbeScoreboardVerdict, number> = { HELPFUL: 0, HARMFUL: 1, NOISY: 2, COLLECTING: 3 };
      return verdictRank[a.verdict] - verdictRank[b.verdict]
        || a.symbol.localeCompare(b.symbol)
        || a.session.localeCompare(b.session)
        || expectedLabel(a.expectedDirection).localeCompare(expectedLabel(b.expectedDirection));
    });
}

/**
 * Translate accountability scoreboard evidence into model-improvement tasks.
 *
 * This is intentionally ACCOUNTABILITY ONLY: it points to future candidate
 * FBE weighting/threshold research. It must never feed Command Center,
 * actionability, alerts, sizing, or execution gates.
 */
export function buildFbeAccuracyRecommendations(rows: readonly FbeScoreboardRow[]): FbeAccuracyRecommendationRow[] {
  return rows.map((row) => {
    if (row.verdict === "HELPFUL") {
      return {
        surface: "accountability_only" as const,
        symbol: row.symbol,
        session: row.session,
        expectedDirection: row.expectedDirection,
        action: "candidate_weight_up" as const,
        priority: "high" as const,
        rationale: "Helpful segment: test higher candidate weight in shadow FBE, not live execution.",
        decided: row.decided,
        decidedWinRate: row.decidedWinRate,
        avgBrier: row.avgBrier,
        avgMoveTicks: row.avgMoveTicks,
      };
    }
    if (row.verdict === "HARMFUL") {
      return {
        surface: "accountability_only" as const,
        symbol: row.symbol,
        session: row.session,
        expectedDirection: row.expectedDirection,
        action: "candidate_weight_down_or_no_edge" as const,
        priority: "high" as const,
        rationale: "Harmful segment: test lower weight or no-edge label in shadow FBE.",
        decided: row.decided,
        decidedWinRate: row.decidedWinRate,
        avgBrier: row.avgBrier,
        avgMoveTicks: row.avgMoveTicks,
      };
    }
    if (row.verdict === "COLLECTING") {
      return {
        surface: "accountability_only" as const,
        symbol: row.symbol,
        session: row.session,
        expectedDirection: row.expectedDirection,
        action: "collect_more_outcomes" as const,
        priority: "low" as const,
        rationale: "Sample is too small for a responsible accuracy change.",
        decided: row.decided,
        decidedWinRate: row.decidedWinRate,
        avgBrier: row.avgBrier,
        avgMoveTicks: row.avgMoveTicks,
      };
    }
    return {
      surface: "accountability_only" as const,
      symbol: row.symbol,
      session: row.session,
      expectedDirection: row.expectedDirection,
      action: "keep_log_only_no_change" as const,
      priority: "medium" as const,
      rationale: "Noisy segment: keep collecting; do not promote, demote, or execute from this bucket yet.",
      decided: row.decided,
      decidedWinRate: row.decidedWinRate,
      avgBrier: row.avgBrier,
      avgMoveTicks: row.avgMoveTicks,
    };
  }).sort((a, b) => {
    const priorityRank = { high: 0, medium: 1, low: 2 } as const;
    return priorityRank[a.priority] - priorityRank[b.priority]
      || a.symbol.localeCompare(b.symbol)
      || a.session.localeCompare(b.session)
      || expectedLabel(a.expectedDirection).localeCompare(expectedLabel(b.expectedDirection));
  });
}

/** Apply read-only table controls without mutating the source rows. */
export function getVisibleFbePromotionReviewRows(
  rows: readonly FbePromotionReviewRow[],
  readinessFilter: FbePromotionReviewFilter,
  sortOrder: FbePromotionReviewSort,
): FbePromotionReviewRow[] {
  const filtered = readinessFilter === "all"
    ? [...rows]
    : rows.filter((row) => row.readiness.status === readinessFilter);

  return filtered.sort((a, b) => {
    if (sortOrder === "sample") {
      return b.actionableCompleted - a.actionableCompleted || comparePromotionReviewRows(a, b);
    }
    if (sortOrder === "win_rate") {
      return (b.decidedWinRate ?? -1) - (a.decidedWinRate ?? -1) || comparePromotionReviewRows(a, b);
    }
    if (sortOrder === "brier") {
      const aBrier = a.avgBrier ?? Number.POSITIVE_INFINITY;
      const bBrier = b.avgBrier ?? Number.POSITIVE_INFINITY;
      return aBrier - bBrier || comparePromotionReviewRows(a, b);
    }
    return comparePromotionReviewRows(a, b);
  });
}

/** Count promotion review rows by readiness without implying approval. */
export function countFbePromotionReviewRowsByStatus(rows: readonly FbePromotionReviewRow[]): Record<FbePromotionReadinessStatus, number> {
  return rows.reduce<Record<FbePromotionReadinessStatus, number>>((counts, row) => {
    counts[row.readiness.status] += 1;
    return counts;
  }, {
    manual_review_candidate: 0,
    not_ready: 0,
    collecting_sample: 0,
  });
}

function summarizeFbeOutcome(row: FbeOutcomeRow): FbePromotionReviewOutcomeSummary {
  return {
    id: row._id ?? `${row.symbol}|${row.session}|${row.horizon_label}|${row.forecast_created_at}|${row.horizon_due_ms}`,
    result: row.directional_result,
    expectedDirection: row.expected_direction,
    stableConfidence: row.forecast_stable_confidence,
    moveTicks: typeof row.move_ticks === "number" && Number.isFinite(row.move_ticks) ? row.move_ticks : null,
    brierScore: typeof row.brier_score === "number" && Number.isFinite(row.brier_score) ? row.brier_score : null,
    completedAt: typeof row.completed_at === "number" && Number.isFinite(row.completed_at)
      ? row.completed_at
      : typeof row.horizon_due_ms === "number" && Number.isFinite(row.horizon_due_ms)
        ? row.horizon_due_ms
        : null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function formatAgeSeconds(value: unknown): string | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (value < 0) return "clock skew";
  if (value < 90) return `${Math.round(value)}s`;
  return `${Math.round(value / 60)}m`;
}

export function buildFbeLayerAttributionRows(rows: FbeOutcomeRow[]): FbeLayerAttributionRow[] {
  const latestBySymbol = new Map<string, FbeOutcomeRow>();
  for (const row of rows) {
    if (!row.layer_attribution_version || !isRecord(row.layer_votes)) continue;
    const current = latestBySymbol.get(row.symbol);
    if (!current || row.forecast_created_at > current.forecast_created_at) latestBySymbol.set(row.symbol, row);
  }

  return Array.from(latestBySymbol.values())
    .sort((a, b) => a.symbol.localeCompare(b.symbol))
    .map((row) => {
      const votes = isRecord(row.layer_votes) ? row.layer_votes : {};
      const topVotes = Object.entries(votes)
        .map(([layer, raw]) => {
          const vote = isRecord(raw) ? raw : {};
          const contribution = typeof vote.contribution === "number" && Number.isFinite(vote.contribution)
            ? vote.contribution
            : null;
          return {
            layer,
            direction: typeof vote.direction === "string" ? vote.direction : "unknown",
            contribution,
          };
        })
        .sort((a, b) => Math.abs(b.contribution ?? 0) - Math.abs(a.contribution ?? 0))
        .slice(0, 4);

      const freshness = isRecord(row.layer_freshness) ? row.layer_freshness : {};
      const freshnessParts = ["current_price", "order_flow", "volume_flow"]
        .map((key) => {
          const item = isRecord(freshness[key]) ? freshness[key] : {};
          const age = formatAgeSeconds(item.age_sec);
          return age ? `${key.replace("_", " ")}: ${age}` : null;
        })
        .filter((v): v is string => Boolean(v));

      return {
        symbol: row.symbol,
        forecastState: row.forecast_stable_state,
        version: row.layer_attribution_version ?? "unknown",
        topVotes,
        freshnessLine: freshnessParts.length ? freshnessParts.join(" · ") : "freshness unavailable",
      };
    });
}

/** Build row-level drilldown evidence from existing read-only outcome rows. */
export function buildFbePromotionReviewEvidence(
  rows: readonly FbeOutcomeRow[],
  reviewRow: FbePromotionReviewRow,
): FbePromotionReviewEvidence {
  const matchingRows = rows.filter((row) => (
    row.symbol === reviewRow.symbol &&
    row.session === reviewRow.session &&
    row.horizon_label === reviewRow.horizon
  ));
  let pending = 0;
  let completed = 0;
  let wins = 0;
  let losses = 0;
  let flats = 0;
  let confidenceSum = 0;
  let confidenceCount = 0;

  for (const row of matchingRows) {
    if (row.status === "completed") completed += 1;
    else pending += 1;
    if (row.directional_result === "win") wins += 1;
    if (row.directional_result === "loss") losses += 1;
    if (row.directional_result === "flat") flats += 1;
    if (Number.isFinite(row.forecast_stable_confidence)) {
      confidenceSum += row.forecast_stable_confidence;
      confidenceCount += 1;
    }
  }

  const summaries = matchingRows.map(summarizeFbeOutcome);
  const worstMisses = summaries
    .filter((row) => row.result === "loss")
    .sort((a, b) => (b.brierScore ?? -1) - (a.brierScore ?? -1) || Math.abs(b.moveTicks ?? 0) - Math.abs(a.moveTicks ?? 0))
    .slice(0, 3);
  const recentOutcomes = summaries
    .filter((row) => row.result !== "pending")
    .sort((a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0))
    .slice(0, 3);

  return {
    total: matchingRows.length,
    pending,
    completed,
    wins,
    losses,
    flats,
    avgStableConfidence: confidenceCount > 0 ? confidenceSum / confidenceCount : null,
    worstMisses,
    recentOutcomes,
  };
}

/**
 * Compact expected-direction split for the selected session/symbol scope.
 * This intentionally stays read-only: it exposes whether FBE's bullish,
 * bearish, or flat calls are accumulating evidence without feeding any gate.
 */
export function buildFbeDirectionSummaries(rows: FbeOutcomeRow[]): FbeDirectionSummary[] {
  type Acc = Omit<FbeDirectionSummary, "decided" | "decidedWinRate" | "avgBrier"> & {
    sumBrier: number;
    nBrier: number;
  };

  const init = (expectedDirection: FbeExpectedDirection): Acc => ({
    expectedDirection,
    total: 0,
    pending: 0,
    completed: 0,
    wins: 0,
    losses: 0,
    flats: 0,
    sumBrier: 0,
    nBrier: 0,
  });

  const map: Record<FbeExpectedDirection, Acc> = {
    up: init("up"),
    down: init("down"),
    flat: init("flat"),
  };

  for (const row of rows) {
    const key = expectedDirectionKey(row.expected_direction);
    if (!key) continue;
    const acc = map[key];
    acc.total += 1;
    if (row.status === "pending") acc.pending += 1;
    if (row.status !== "completed") continue;

    acc.completed += 1;
    if (row.directional_result === "win") acc.wins += 1;
    else if (row.directional_result === "loss") acc.losses += 1;
    else if (row.directional_result === "flat") acc.flats += 1;
    if (typeof row.brier_score === "number" && Number.isFinite(row.brier_score)) {
      acc.sumBrier += row.brier_score;
      acc.nBrier += 1;
    }
  }

  return [map.up, map.down, map.flat].map((acc) => {
    const decided = acc.wins + acc.losses;
    return {
      expectedDirection: acc.expectedDirection,
      total: acc.total,
      pending: acc.pending,
      completed: acc.completed,
      wins: acc.wins,
      losses: acc.losses,
      flats: acc.flats,
      decided,
      decidedWinRate: decided > 0 ? acc.wins / decided : null,
      avgBrier: acc.nBrier > 0 ? acc.sumBrier / acc.nBrier : null,
    };
  });
}

/**
 * Per-horizon read-only breakdown for the selected session/symbol scope.
 * Unlike calibration buckets, this includes sub-50% and flat rows so the
 * accountability view shows where outcomes are accumulating or still pending.
 */
export function buildFbeHorizonBreakdownSummaries(rows: FbeOutcomeRow[]): FbeHorizonBreakdownSummary[] {
  type Acc = Omit<FbeHorizonBreakdownSummary, "decided" | "decidedWinRate" | "avgBrier"> & {
    sumBrier: number;
    nBrier: number;
  };

  const init = (horizon: string): Acc => ({
    horizon,
    total: 0,
    pending: 0,
    completed: 0,
    wins: 0,
    losses: 0,
    flats: 0,
    sumBrier: 0,
    nBrier: 0,
  });

  const map = new Map<string, Acc>();
  for (const horizon of HORIZON_ORDER) map.set(horizon, init(horizon));

  for (const row of rows) {
    const acc = map.get(row.horizon_label) ?? init(row.horizon_label);
    map.set(row.horizon_label, acc);
    acc.total += 1;
    if (row.status === "pending") acc.pending += 1;
    if (row.status !== "completed") continue;

    acc.completed += 1;
    if (row.directional_result === "win") acc.wins += 1;
    else if (row.directional_result === "loss") acc.losses += 1;
    else if (row.directional_result === "flat") acc.flats += 1;
    if (typeof row.brier_score === "number" && Number.isFinite(row.brier_score)) {
      acc.sumBrier += row.brier_score;
      acc.nBrier += 1;
    }
  }

  const horizonIdx = (h: string) => {
    const i = HORIZON_ORDER.indexOf(h);
    return i === -1 ? HORIZON_ORDER.length : i;
  };

  return [...map.values()]
    .sort((a, b) => horizonIdx(a.horizon) - horizonIdx(b.horizon) || a.horizon.localeCompare(b.horizon))
    .map((acc) => {
      const decided = acc.wins + acc.losses;
      return {
        horizon: acc.horizon,
        total: acc.total,
        pending: acc.pending,
        completed: acc.completed,
        wins: acc.wins,
        losses: acc.losses,
        flats: acc.flats,
        decided,
        decidedWinRate: decided > 0 ? acc.wins / decided : null,
        avgBrier: acc.nBrier > 0 ? acc.sumBrier / acc.nBrier : null,
      };
    });
}

export interface SymbolLiveState {
  symbol: string;
  forecast: FbeForecastMeta | null;
  horizons: Record<string, FbeOutcomeRow | null>;
}

type LiveForecastSnapshotRecord = Partial<FbeForecastMeta> & Record<string, unknown>;

function asLiveForecastSnapshot(row: unknown): LiveForecastSnapshotRecord | null {
  if (!row || typeof row !== "object") return null;
  const record = row as LiveForecastSnapshotRecord;
  return record.forecast_stable_state !== undefined || record.forecast_raw_state !== undefined
    ? record
    : null;
}

function liveSnapshotToForecastMeta(symbol: string, row: unknown, session: "asia" | "us"): FbeForecastMeta | null {
  const snapshot = asLiveForecastSnapshot(row);
  if (!snapshot) return null;
  if (!isScoreableFbeSessionForecast(snapshot)) return null;
  const snapshotSession = typeof snapshot.session === "string" ? snapshot.session.toLowerCase() : undefined;
  if (snapshotSession && snapshotSession !== session) return null;
  const forecastSessionId = typeof snapshot.forecast_session_id === "string"
    ? snapshot.forecast_session_id.toLowerCase()
    : undefined;
  if (forecastSessionId && !forecastSessionId.startsWith(`${session}:`)) return null;
  const createdAt = typeof snapshot.forecast_generated_at === "number" && Number.isFinite(snapshot.forecast_generated_at)
    ? snapshot.forecast_generated_at
    : typeof snapshot.created_at === "number" && Number.isFinite(snapshot.created_at)
      ? snapshot.created_at
      : undefined;
  if (createdAt === undefined) return null;
  return {
    symbol,
    session: snapshot.session ?? session,
    session_label: snapshot.session_label,
    date: snapshot.date,
    forecast_created_at: createdAt,
    forecast_created_at_iso: snapshot.forecast_created_at_iso,
    forecast_raw_state: snapshot.forecast_raw_state,
    forecast_raw_confidence: snapshot.forecast_raw_confidence,
    forecast_raw_probs: snapshot.forecast_raw_probs,
    forecast_stable_state: snapshot.forecast_stable_state,
    forecast_stable_confidence: snapshot.forecast_stable_confidence,
    forecast_transition_probability: snapshot.forecast_transition_probability,
    forecast_state_age_min: snapshot.forecast_state_age_min,
    forecast_flip_suppressed: snapshot.forecast_flip_suppressed,
    forecast_stability_score: snapshot.forecast_stability_score,
    forecast_reason: snapshot.forecast_reason,
    forecast_engine_version: snapshot.forecast_engine_version,
    forecast_trader_bias: snapshot.forecast_trader_bias,
    forecast_trade_mode: snapshot.forecast_trade_mode,
    forecast_preferred_setups: snapshot.forecast_preferred_setups,
    forecast_avoid_setups: snapshot.forecast_avoid_setups,
    forecast_guidance_reason: snapshot.forecast_guidance_reason,
    forecast_guidance_contract: snapshot.forecast_guidance_contract,
    forecast_guidance_execution_approved: snapshot.forecast_guidance_execution_approved,
    forecast_contract: snapshot.forecast_contract,
    forecast_anchor_type: snapshot.forecast_anchor_type,
    forecast_session_id: snapshot.forecast_session_id,
    created_at: snapshot.created_at,
    updated_at: snapshot.updated_at,
  };
}

/**
 * For each dashboard symbol, return the current FBE forecast and the outcome
 * row per horizon for that same forecast timestamp.
 *
 * Source ownership:
 * - Current live state comes from liveMarketSnapshots/getLatestIntelBySymbol.
 * - Accountability horizons come from fbeForecastOutcomes.
 *
 * If the tracker misses a write for a symbol, the live forecast still renders
 * with empty horizon badges instead of falsely saying "no forecast".
 */
export function buildSymbolLiveStates(
  rows: FbeOutcomeRow[],
  session: "asia" | "us",
  liveForecasts: Record<string, unknown> | null = null,
): SymbolLiveState[] {
  const filtered = rows.filter((r) => r.session === session && isScoreableSessionForecastRow(r));

  // Latest outcome forecast_created_at per symbol as a fallback when no live
  // snapshot is available (historical/tests/off-hours).
  const latestOutcomePerSymbol = new Map<string, number>();
  for (const row of filtered) {
    const cur = latestOutcomePerSymbol.get(row.symbol);
    if (cur === undefined || row.forecast_created_at > cur) {
      latestOutcomePerSymbol.set(row.symbol, row.forecast_created_at);
    }
  }

  return DASHBOARD_SYMBOLS.map((symbol) => {
    const liveForecast = liveSnapshotToForecastMeta(symbol, liveForecasts?.[symbol], session);
    const latest = liveForecast?.forecast_created_at ?? latestOutcomePerSymbol.get(symbol);
    if (latest === undefined) {
      return {
        symbol,
        forecast: null,
        horizons: Object.fromEntries(HORIZON_ORDER.map((h) => [h, null])),
      } as SymbolLiveState;
    }

    const horizonRows = filtered.filter(
      (r) => r.symbol === symbol && r.forecast_created_at === latest,
    );
    // Use the row with the earliest horizon as the fallback "forecast meta"
    // carrier — all rows share forecast_* metadata for one forecast snapshot.
    const sorted = [...horizonRows].sort(
      (a, b) => HORIZON_ORDER.indexOf(a.horizon_label) - HORIZON_ORDER.indexOf(b.horizon_label),
    );
    const horizons: Record<string, FbeOutcomeRow | null> = Object.fromEntries(
      HORIZON_ORDER.map((h) => [h, null]),
    );
    for (const row of horizonRows) {
      horizons[row.horizon_label] = row;
    }
    return { symbol, forecast: liveForecast ?? sorted[0] ?? horizonRows[0], horizons } as SymbolLiveState;
  });
}

/* ── Sub-components ── */

function HorizonBadge({ row, horizon }: { row: FbeOutcomeRow | null; horizon: string }) {
  const label = horizonShort(horizon);
  if (!row) {
    return (
      <span className="fbe-acc-horizon fbe-acc-horizon-missing" aria-label={`${label} no data`}>
        <strong>{label}</strong>
        <em>—</em>
      </span>
    );
  }
  if (row.status === "pending") {
    return (
      <span className="fbe-acc-horizon fbe-acc-horizon-pending" aria-label={`${label} pending`}>
        <strong>{label}</strong>
        <em>pending</em>
      </span>
    );
  }
  const result = row.directional_result;
  const ticks = formatTicks(row.move_ticks ?? null);
  return (
    <span
      className={`fbe-acc-horizon fbe-acc-horizon-${result}`}
      style={{ color: RESULT_COLORS[result] ?? RESULT_COLORS.flat }}
      aria-label={`${label} ${result} ${ticks}`}
    >
      <strong>{label}</strong>
      <em>{result.toUpperCase()} {ticks}</em>
    </span>
  );
}

function SymbolLiveRow({ state, sessionToken }: { state: SymbolLiveState; sessionToken: "asia" | "us" }) {
  const { symbol, forecast, horizons } = state;
  const view = forecast ? buildFbeForecastView(forecast) : undefined;
  const guidance = forecast ? buildFbeCardGuidance(forecast) : undefined;
  const sessionPretty = sessionLabel(sessionToken, forecast?.session_label);
  return (
    <div className="fbe-acc-symbol-row" data-symbol={symbol}>
      <div className="fbe-acc-symbol-head">
        <span className="fbe-acc-symbol">{symbol}</span>
        <span className="fbe-acc-session-pill">{sessionPretty}</span>
        {forecast && view ? (
          <>
            <span className="fbe-acc-stable" style={{ color: view.stateColor }}>
              <strong>FBE:</strong> {view.primaryLabel}
            </span>
            {view.lowConfidenceDirectional && (
              <span className="fbe-acc-age">stable prior {view.stableDetail}</span>
            )}
            <span className="fbe-acc-age">{view.rawLine}</span>
            {forecast.forecast_flip_suppressed && (
              <span className="fbe-acc-flip">flip suppressed</span>
            )}
          </>
        ) : (
          <span className="fbe-acc-missing">No live FBE forecast in snapshot for this session</span>
        )}
      </div>
      <div className="fbe-acc-horizon-row">
        {HORIZON_ORDER.map((h) => (
          <HorizonBadge key={h} row={horizons[h] ?? null} horizon={h} />
        ))}
      </div>
      {forecast && guidance ? (
        <div className="fbe-acc-calib-detail" aria-label={`${symbol} FBE forecast provenance`}>
          <span>{guidance.dataLine}</span>
          <span>{guidance.calibrationLine}</span>
          <span>{guidance.governanceLine}</span>
        </div>
      ) : null}
    </div>
  );
}

function CalibrationRow({ bucket }: { bucket: CalibrationBucket }) {
  const pretty = sessionLabel(bucket.session);
  const direction = expectedLabel(bucket.expectedDirection);
  const evidence = calibrationEvidenceLabel(bucket);
  const directionColor =
    bucket.expectedDirection === "up"
      ? STATE_COLORS.bullish
      : bucket.expectedDirection === "down"
        ? STATE_COLORS.bearish
        : STATE_COLORS.flat;
  const follow = bucket.followThrough;
  const followColor =
    follow === null
      ? RESULT_COLORS.pending
      : follow >= 0.55
        ? RESULT_COLORS.win
        : follow <= 0.45
          ? RESULT_COLORS.loss
          : RESULT_COLORS.flat;
  return (
    <div className="fbe-acc-calib-row">
      <div className="fbe-acc-calib-head">
        <span className="fbe-acc-symbol-small">{bucket.symbol}</span>
        <span className="fbe-acc-session-pill">{pretty}</span>
        <span className="fbe-acc-calib-dir" style={{ color: directionColor }}>
          {direction} {bucket.confidenceBucket} bucket
        </span>
        <span className="fbe-acc-calib-horizon">{horizonShort(bucket.horizon)}</span>
      </div>
      <div className="fbe-acc-calib-detail">
        <span>{evidence}</span>
        <span style={{ color: followColor }}>
          follow-through {formatPct(follow)}
        </span>
        <span>decided {formatPct(bucket.decidedWinRate)}</span>
        <span>avg {formatTicks(bucket.avgTicks)}</span>
        <span>n={bucket.nCompleted}{bucket.n > bucket.nCompleted ? ` (+${bucket.n - bucket.nCompleted} pending)` : ""}</span>
        <span>Brier {formatBrier(bucket.avgBrier)}</span>
      </div>
    </div>
  );
}

function FbeSummaryCard({ summary }: { summary: FbeSessionSummary }) {
  const best = summary.bestHorizon
    ? `${horizonShort(summary.bestHorizon.horizon)} ${formatPct(summary.bestHorizon.decidedWinRate)} (${summary.bestHorizon.decided})`
    : "—";
  const worst = summary.worstHorizon
    ? `${horizonShort(summary.worstHorizon.horizon)} ${formatPct(summary.worstHorizon.decidedWinRate)} (${summary.worstHorizon.decided})`
    : "—";
  const readiness = buildFbePromotionReadiness(summary);

  return (
    <div className="fbe-acc-summary-card" aria-label="FBE summary">
      <div className="fbe-acc-summary-head">
        <span>FBE summary</span>
        <strong>{summary.evidenceLabel}</strong>
      </div>
      <div className="fbe-acc-summary-grid">
        <div>
          <span>Completed</span>
          <strong>{summary.completed}</strong>
        </div>
        <div>
          <span>Pending</span>
          <strong>{summary.pending}</strong>
        </div>
        <div>
          <span>Decided WR</span>
          <strong>{formatPct(summary.decidedWinRate)}</strong>
        </div>
        <div>
          <span>Flat rate</span>
          <strong>{formatPct(summary.flatRate)}</strong>
        </div>
        <div>
          <span>Avg Brier</span>
          <strong>{formatBrier(summary.avgBrier)}</strong>
        </div>
        <div>
          <span>W/L/F</span>
          <strong>{summary.wins}/{summary.losses}/{summary.flats}</strong>
        </div>
        <div>
          <span>Best horizon</span>
          <strong>{best}</strong>
        </div>
        <div>
          <span>Weakest horizon</span>
          <strong>{worst}</strong>
        </div>
      </div>
      <div className="fbe-acc-promotion-readiness" aria-label="FBE promotion readiness">
        <span>Promotion readiness</span>
        <strong>{readiness.label}</strong>
        <small>{readiness.reasons.join(" · ")}</small>
      </div>
    </div>
  );
}

function FbeDirectionBreakdown({ directions }: { directions: FbeDirectionSummary[] }) {
  return (
    <div className="fbe-acc-direction-card" aria-label="FBE expected direction breakdown">
      <div className="fbe-acc-section-title">
        Expected direction breakdown
        <span className="fbe-acc-section-sub">selected session/symbol · all horizons</span>
      </div>
      <div className="fbe-acc-direction-grid">
        {directions.map((row) => (
          <div key={row.expectedDirection} className="fbe-acc-direction-cell">
            <strong>{expectedLabel(row.expectedDirection)}</strong>
            <span>{row.completed} completed · {row.pending} pending</span>
            <span>{formatPct(row.decidedWinRate)} decided WR · {row.wins}/{row.losses}/{row.flats} W/L/F</span>
            <small>Avg Brier {formatBrier(row.avgBrier)} · {row.total} rows</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function FbeHorizonBreakdown({ horizons }: { horizons: FbeHorizonBreakdownSummary[] }) {
  return (
    <div className="fbe-acc-breakdown-card" aria-label="FBE horizon breakdown">
      <div className="fbe-acc-section-title">
        Horizon breakdown
        <span className="fbe-acc-section-sub">selected session/symbol · all confidence levels</span>
      </div>
      <div className="fbe-acc-breakdown-grid">
        {horizons.map((row) => (
          <div key={row.horizon} className="fbe-acc-breakdown-cell">
            <strong>{horizonShort(row.horizon)}</strong>
            <span>{row.completed} completed · {row.pending} pending</span>
            <span>{formatPct(row.decidedWinRate)} decided WR · {row.wins}/{row.losses}/{row.flats} W/L/F</span>
            <small>Avg Brier {formatBrier(row.avgBrier)} · {row.total} rows</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function FbeScoreboard({ rows }: { rows: FbeScoreboardRow[] }) {
  return (
    <div className="fbe-acc-review-card" aria-label="FBE scoreboard">
      <div className="fbe-acc-section-title">
        FBE scoreboard
        <span className="fbe-acc-section-sub">symbol · session · bias · manual edge quality</span>
      </div>
      {rows.length === 0 ? (
        <div className="fbe-acc-review-empty">No FBE scoreboard rows yet for this scope.</div>
      ) : (
        <div className="fbe-acc-review-rows">
          {rows.map((row) => (
            <div key={`${row.symbol}|${row.session}|${row.expectedDirection}`} className={`fbe-acc-review-row fbe-acc-score-${row.verdict.toLowerCase()}`}>
              <div className="fbe-acc-review-main">
                <strong>{row.symbol} · {sessionLabel(row.session)} · {expectedLabel(row.expectedDirection)}</strong>
                <span>{row.verdict}</span>
              </div>
              <div className="fbe-acc-review-metrics">
                <span>{row.completed} completed</span>
                <span>{row.pending} pending</span>
                <span>{formatPct(row.decidedWinRate)} WR</span>
                <span>Avg {formatTicks(row.avgMoveTicks)}</span>
                <span>Brier {formatBrier(row.avgBrier)}</span>
              </div>
              <small>{row.actionableCompleted} actionable · {row.decided} decided · {row.wins}/{row.losses}/{row.flats} W/L/F · LOG-ONLY</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function recommendationActionLabel(action: FbeAccuracyRecommendationAction): string {
  if (action === "candidate_weight_up") return "Test weight up";
  if (action === "candidate_weight_down_or_no_edge") return "Test weight down / no-edge";
  if (action === "collect_more_outcomes") return "Collect more outcomes";
  return "Keep LOG-ONLY";
}

function FbeAccuracyRecommendations({ rows }: { rows: FbeAccuracyRecommendationRow[] }) {
  return (
    <div className="fbe-acc-review-card" aria-label="FBE accuracy recommendations">
      <div className="fbe-acc-section-title">
        Accuracy recommendations
        <span className="fbe-acc-section-sub">accountability-only · candidate FBE research · never Command Center</span>
      </div>
      {rows.length === 0 ? (
        <div className="fbe-acc-review-empty">No scoreable FBE evidence yet. Keep collecting outcomes.</div>
      ) : (
        <div className="fbe-acc-review-rows">
          {rows.slice(0, 10).map((row) => (
            <div key={`${row.symbol}|${row.session}|${row.expectedDirection}|${row.action}`} className={`fbe-acc-review-row fbe-acc-reco-${row.priority}`}>
              <div className="fbe-acc-review-main">
                <strong>{row.symbol} · {sessionLabel(row.session)} · {expectedLabel(row.expectedDirection)}</strong>
                <span>{recommendationActionLabel(row.action)}</span>
              </div>
              <div className="fbe-acc-review-metrics">
                <span>{row.priority.toUpperCase()} priority</span>
                <span>{row.decided} decided</span>
                <span>{formatPct(row.decidedWinRate)} WR</span>
                <span>Avg {formatTicks(row.avgMoveTicks)}</span>
                <span>Brier {formatBrier(row.avgBrier)}</span>
              </div>
              <small>{row.rationale} · {row.surface.replace(/_/g, " ")}</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FbePromotionOutcomeList({ title, rows }: { title: string; rows: FbePromotionReviewOutcomeSummary[] }) {
  return (
    <div className="fbe-acc-review-evidence-list">
      <strong>{title}</strong>
      {rows.length === 0 ? (
        <span>No rows yet</span>
      ) : rows.map((row) => (
        <span key={row.id}>
          {row.result.toUpperCase()} · {expectedLabel(row.expectedDirection)} · {formatConfidence(row.stableConfidence)} confidence · {formatTicks(row.moveTicks)} · Brier {formatBrier(row.brierScore)}
        </span>
      ))}
    </div>
  );
}

function FbePromotionReviewTable({ rows, evidenceRows }: { rows: FbePromotionReviewRow[]; evidenceRows: FbeOutcomeRow[] }) {
  const [readinessFilter, setReadinessFilter] = useState<FbePromotionReviewFilter>("all");
  const [sortOrder, setSortOrder] = useState<FbePromotionReviewSort>("readiness");
  const visibleRows = useMemo(
    () => getVisibleFbePromotionReviewRows(rows, readinessFilter, sortOrder),
    [rows, readinessFilter, sortOrder],
  );
  const readinessCounts = useMemo(() => countFbePromotionReviewRowsByStatus(rows), [rows]);
  const evidenceByKey = useMemo(() => {
    const map = new Map<string, FbePromotionReviewEvidence>();
    for (const row of rows) {
      map.set(`${row.symbol}|${row.session}|${row.horizon}`, buildFbePromotionReviewEvidence(evidenceRows, row));
    }
    return map;
  }, [evidenceRows, rows]);

  return (
    <div className="fbe-acc-review-card" aria-label="FBE promotion review table">
      <div className="fbe-acc-section-title">
        Promotion review table
        <span className="fbe-acc-section-sub">symbol · session · horizon · LOG-ONLY</span>
      </div>
      <div className="fbe-acc-review-controls">
        <label>
          <span>Readiness</span>
          <select
            aria-label="Review readiness filter"
            value={readinessFilter}
            onChange={(e) => setReadinessFilter(e.target.value as FbePromotionReviewFilter)}
          >
            <option value="all">All</option>
            <option value="manual_review_candidate">Manual review</option>
            <option value="not_ready">Not ready</option>
            <option value="collecting_sample">Collecting sample</option>
          </select>
        </label>
        <label>
          <span>Sort</span>
          <select
            aria-label="Review sort order"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as FbePromotionReviewSort)}
          >
            <option value="readiness">Closest to promotion</option>
            <option value="sample">Sample size</option>
            <option value="win_rate">Win rate</option>
            <option value="brier">Brier score</option>
          </select>
        </label>
      </div>
      <div className="fbe-acc-review-counts" aria-label="FBE promotion readiness counts">
        <span><strong>{readinessCounts.manual_review_candidate}</strong> manual review</span>
        <span><strong>{readinessCounts.not_ready}</strong> not ready</span>
        <span><strong>{readinessCounts.collecting_sample}</strong> collecting sample</span>
      </div>
      {rows.length === 0 ? (
        <div className="fbe-acc-review-empty">No scoreable FBE rows yet for this scope.</div>
      ) : visibleRows.length === 0 ? (
        <div className="fbe-acc-review-empty">No FBE review rows match this readiness filter.</div>
      ) : (
        <div className="fbe-acc-review-rows">
          {visibleRows.map((row) => (
            <div
              key={`${row.symbol}|${row.session}|${row.horizon}`}
              className={`fbe-acc-review-row fbe-acc-review-${row.readiness.status}`}
            >
              <div className="fbe-acc-review-main">
                <strong>{row.symbol} · {sessionLabel(row.session)} · {horizonShort(row.horizon)}</strong>
                <span>{row.readiness.label}</span>
              </div>
              <div className="fbe-acc-review-metrics">
                <span>{row.actionableCompleted}/{PROMOTION_MIN_ACTIONABLE_COMPLETED} actionable</span>
                <span>{row.decided} decided</span>
                <span>{formatPct(row.decidedWinRate)} WR</span>
                <span>Brier {formatBrier(row.avgBrier)}</span>
              </div>
              <small>{row.readiness.reasons.join(" · ")}</small>
              {(() => {
                const evidence = evidenceByKey.get(`${row.symbol}|${row.session}|${row.horizon}`);
                if (!evidence) return null;
                return (
                  <details className="fbe-acc-review-evidence">
                    <summary>Evidence drilldown · LOG-ONLY</summary>
                    <div className="fbe-acc-review-evidence-grid" aria-label={`${row.symbol} ${sessionLabel(row.session)} ${horizonShort(row.horizon)} evidence`}>
                      <span><strong>{evidence.total}</strong> total rows</span>
                      <span><strong>{evidence.completed}</strong> completed · <strong>{evidence.pending}</strong> pending</span>
                      <span><strong>{evidence.wins}/{evidence.losses}/{evidence.flats}</strong> W/L/F</span>
                      <span><strong>{formatConfidence(evidence.avgStableConfidence)}</strong> avg confidence</span>
                    </div>
                    <div className="fbe-acc-review-evidence-columns">
                      <FbePromotionOutcomeList title="Worst misses" rows={evidence.worstMisses} />
                      <FbePromotionOutcomeList title="Recent outcomes" rows={evidence.recentOutcomes} />
                    </div>
                  </details>
                );
              })()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FbeLayerAttributionTable({ rows }: { rows: FbeLayerAttributionRow[] }) {
  return (
    <div className="fbe-acc-review-card" aria-label="FBE layer attribution">
      <div className="fbe-acc-section-title">
        Layer attribution
        <span className="fbe-acc-section-sub">LOG-ONLY · input freshness and top vote contributors</span>
      </div>
      {rows.length === 0 ? (
        <div className="fbe-acc-review-empty">No layer attribution rows yet. New FBE outcome rows will populate this section.</div>
      ) : (
        <div className="fbe-acc-review-rows">
          {rows.map((row) => (
            <div key={row.symbol} className="fbe-acc-review-row">
              <div className="fbe-acc-review-main">
                <strong>{row.symbol} · {stateLabel(row.forecastState)}</strong>
                <span>{row.freshnessLine}</span>
                <small>{row.version}</small>
              </div>
              <div className="fbe-acc-review-metrics">
                {row.topVotes.map((vote) => (
                  <span key={vote.layer}>
                    {vote.layer}: {vote.direction} {vote.contribution === null ? "" : vote.contribution.toFixed(3)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main panel ── */

/**
 * Build the Convex query args for ``getFbeForecastOutcomes`` from the
 * panel's filter state. Exported so the container can call it and tests
 * can pin the shape we hand to Convex.
 *
 *   * ``session`` is ALWAYS included — the dashboard query is capped at
 *     500 rows; without a session scope a noisy session would silently
 *     evict the selected one from the result.
 *   * ``symbol`` is included only when the filter is not "all" — Convex
 *     handles an absent ``symbol`` by returning every dashboard symbol,
 *     which is what we want for the "All" view.
 *   * ``limit`` is fixed at 500 (the Convex cap) so an active session
 *     always returns every row we can possibly need for that scope.
 */
export function buildFbeOutcomesQueryArgs(
  sessionTab: "asia" | "us",
  symbolFilter: "all" | string,
): { session: string; symbol?: string; limit: number } {
  const args: { session: string; symbol?: string; limit: number } = {
    session: sessionTab,
    limit: 500,
  };
  if (symbolFilter !== "all") {
    args.symbol = symbolFilter;
  }
  return args;
}

export function FbeAccountabilityPanel({
  state,
  liveForecasts,
  sessionTab: controlledSessionTab,
  onSessionTab,
  symbolFilter: controlledSymbolFilter,
  onSymbolFilter,
}: FbeAccountabilityPanelProps) {
  // Controlled vs uncontrolled: when the container hoists state to scope
  // the Convex query, it passes both ``sessionTab`` and ``onSessionTab``
  // (and the same for ``symbolFilter``). When tests render the panel
  // alone with synthetic data, they omit those props and the panel falls
  // back to its own ``useState`` — preserving the legacy test contract.
  const [internalSessionTab, setInternalSessionTab] = useState<"asia" | "us">("us");
  const [internalSymbolFilter, setInternalSymbolFilter] = useState<"all" | string>("all");
  const [horizonFilter, setHorizonFilter] = useState<string>(HORIZON_DEFAULT_FILTER);

  const sessionTab = controlledSessionTab ?? internalSessionTab;
  const symbolFilter = controlledSymbolFilter ?? internalSymbolFilter;

  // Cache the resolved setters so memoised children don't see a fresh
  // identity on every render. ``useCallback`` would re-allocate when the
  // caller-provided handler changes, which is exactly the behaviour we
  // want here.
  const handleSessionTab = useCallback(
    (next: "asia" | "us") => {
      if (onSessionTab) onSessionTab(next);
      else setInternalSessionTab(next);
    },
    [onSessionTab],
  );
  const handleSymbolFilter = useCallback(
    (next: "all" | string) => {
      if (onSymbolFilter) onSymbolFilter(next);
      else setInternalSymbolFilter(next);
    },
    [onSymbolFilter],
  );

  // Memoise the "success → rows" projection so downstream memo deps stay stable
  // across renders. A bare `state.status === "success" ? state.data : []`
  // produces a fresh `[]` on every error/loading render, which triggers
  // useMemo work below for no reason.
  const rows = useMemo<FbeOutcomeRow[]>(
    () => (state.status === "success" && state.data ? state.data.filter(isScoreableSessionForecastRow) : []),
    [state],
  );

  // Defensive client-side session filter. In the container-driven flow the
  // query is already scoped to ``sessionTab`` so this is a no-op. In the
  // legacy uncontrolled mode (used by panel-level tests with mixed
  // sessions) this still keeps each tab clean.
  const filteredForSession = useMemo(() => rows.filter((r) => r.session === sessionTab), [rows, sessionTab]);

  // Symbol filter applies to BOTH live-state rows and calibration buckets
  // (QST-345 review FIX FIRST blocker 3). Selecting "NQ" shows only the
  // NQ live row and only NQ calibration buckets.
  const filteredForSymbol = useMemo(
    () => (symbolFilter === "all" ? filteredForSession : filteredForSession.filter((r) => r.symbol === symbolFilter)),
    [filteredForSession, symbolFilter],
  );

  const filteredRows = useMemo(() => {
    if (horizonFilter === "all") return filteredForSymbol;
    return filteredForSymbol.filter((r) => r.horizon_label === horizonFilter);
  }, [filteredForSymbol, horizonFilter]);

  const liveStates = useMemo(() => {
    const all = buildSymbolLiveStates(filteredForSession, sessionTab, liveForecasts ?? null);
    return symbolFilter === "all" ? all : all.filter((s) => s.symbol === symbolFilter);
  }, [filteredForSession, liveForecasts, sessionTab, symbolFilter]);
  const summary = useMemo(() => buildFbeSummary(filteredForSymbol), [filteredForSymbol]);
  const directionSummaries = useMemo(() => buildFbeDirectionSummaries(filteredForSymbol), [filteredForSymbol]);
  const horizonSummaries = useMemo(() => buildFbeHorizonBreakdownSummaries(filteredForSymbol), [filteredForSymbol]);
  const scoreboardRows = useMemo(() => buildFbeScoreboardRows(filteredForSymbol), [filteredForSymbol]);
  const accuracyRecommendations = useMemo(() => buildFbeAccuracyRecommendations(scoreboardRows), [scoreboardRows]);
  const promotionReviewRows = useMemo(() => buildFbePromotionReviewRows(filteredForSymbol), [filteredForSymbol]);
  const layerAttributionRows = useMemo(() => buildFbeLayerAttributionRows(filteredForSymbol), [filteredForSymbol]);
  const buckets = useMemo(() => buildCalibrationBuckets(filteredRows), [filteredRows]);

  if (state.status === "loading") {
    return (
      <PanelShell sessionTab={sessionTab} onSessionTab={handleSessionTab}>
        <LoadingState label="FBE accountability" block />
      </PanelShell>
    );
  }

  if (state.status === "error") {
    return (
      <PanelShell sessionTab={sessionTab} onSessionTab={handleSessionTab}>
        <ErrorState error={state.error} onRetry={state.retry} label="FBE accountability" block />
      </PanelShell>
    );
  }

  return (
    <PanelShell sessionTab={sessionTab} onSessionTab={handleSessionTab}>
      <div className="fbe-acc-filters">
        <label className="fbe-acc-filter">
          <span>Symbol</span>
          <select value={symbolFilter} onChange={(e) => handleSymbolFilter(e.target.value as "all" | string)}>
            <option value="all">All</option>
            {DASHBOARD_SYMBOLS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <label className="fbe-acc-filter">
          <span>Horizon</span>
          <select value={horizonFilter} onChange={(e) => setHorizonFilter(e.target.value)}>
            <option value="all">All</option>
            {HORIZON_ORDER.map((h) => (
              <option key={h} value={h}>{horizonShort(h)}</option>
            ))}
          </select>
        </label>
      </div>

      <FbeSummaryCard summary={summary} />
      <FbeDirectionBreakdown directions={directionSummaries} />
      <FbeHorizonBreakdown horizons={horizonSummaries} />
      <FbeScoreboard rows={scoreboardRows} />
      <FbeAccuracyRecommendations rows={accuracyRecommendations} />
      <FbePromotionReviewTable rows={promotionReviewRows} evidenceRows={filteredForSymbol} />
      <FbeLayerAttributionTable rows={layerAttributionRows} />

      <div className="fbe-acc-section-title">
        Current session live state
        {symbolFilter !== "all" && (
          <span className="fbe-acc-section-sub">{symbolFilter} only</span>
        )}
      </div>
      <div className="fbe-acc-live">
        {liveStates.map((s) => (
          <SymbolLiveRow key={s.symbol} state={s} sessionToken={sessionTab} />
        ))}
      </div>

      <div className="fbe-acc-section-title">
        Calibration buckets
        <span className="fbe-acc-section-sub">
          {symbolFilter !== "all" ? `${symbolFilter} · ` : ""}{horizonFilter !== "all" ? `${horizonShort(horizonFilter)} horizon` : "all horizons"}
        </span>
      </div>
      {buckets.length === 0 ? (
        <EmptyState
          message={
            filteredRows.length === 0
              ? "No completed FBE forecasts yet for this session."
              : "No completed rows ≥50% confidence to grade yet."
          }
          block
        />
      ) : (
        <div className="fbe-acc-calib">
          {buckets.map((b) => (
            <CalibrationRow
              key={`${b.symbol}|${b.session}|${b.expectedDirection}|${b.confidenceBucket}|${b.horizon}`}
              bucket={b}
            />
          ))}
        </div>
      )}
    </PanelShell>
  );
}

/**
 * Container that owns the session/symbol filter state and re-keys the
 * Convex query whenever they change. This is the component App.tsx
 * mounts in the dashboard — see ``FbeAccountabilityPanel`` for the
 * presentational sibling that tests render directly.
 *
 * The mandatory LOG-ONLY disclaimer is rendered by ``PanelShell``, which
 * wraps every status (loading/error/empty/success) — see Fix #2 in the
 * file header docblock.
 */
export function FbeAccountabilityPanelContainer() {
  const [sessionTab, setSessionTab] = useState<"asia" | "us">(() => defaultDashboardSession());
  const [symbolFilter, setSymbolFilter] = useState<"all" | string>("all");

  // Re-key the Convex subscription on every (sessionTab, symbolFilter)
  // change so the 500-row cap is spent on the SELECTED session/symbol
  // slice rather than on the whole table.
  const args = useMemo(
    () => buildFbeOutcomesQueryArgs(sessionTab, symbolFilter),
    [sessionTab, symbolFilter],
  );
  const state = useQueryWithState(api.queries.getFbeForecastOutcomes, args) as QueryState<FbeOutcomeRow[]>;
  const liveState = useQueryWithState(api.queries.getLatestIntelBySymbol, { session: sessionTab }) as QueryState<Record<string, unknown>>;
  const liveForecasts = liveState.status === "success" ? liveState.data : null;

  return (
    <FbeAccountabilityPanel
      state={state}
      liveForecasts={liveForecasts}
      sessionTab={sessionTab}
      onSessionTab={setSessionTab}
      symbolFilter={symbolFilter}
      onSymbolFilter={setSymbolFilter}
    />
  );
}

/**
 * Shared layout shell for every render path. Keeps the panel chrome
 * (header, session tabs, LOG-ONLY disclaimer) identical across
 * loading / error / empty / success so the user never sees the panel
 * without its FBE LOG-ONLY caveat.
 */
function PanelShell({
  sessionTab,
  onSessionTab,
  children,
}: {
  sessionTab: "asia" | "us";
  onSessionTab: (s: "asia" | "us") => void;
  children: ReactNode;
}) {
  return (
    <section className="fbe-acc-panel" aria-label="FBE session accountability">
      <PanelHeader sessionTab={sessionTab} onSessionTab={onSessionTab} />
      <div className="fbe-acc-disclaimer">FBE LOG-ONLY · Not used for execution</div>
      {children}
    </section>
  );
}

function PanelHeader({
  sessionTab,
  onSessionTab,
}: {
  sessionTab: "asia" | "us";
  onSessionTab: (s: "asia" | "us") => void;
}) {
  const activeForecastLabel = sessionForecastLabel(sessionTab);

  return (
    <div className="fbe-acc-header">
      <div className="fbe-acc-title">
        <h3>{activeForecastLabel} Accountability</h3>
        <p>
          Scores only anchored session-open FBE forecasts. Rolling Kronos/HMM daily, 4H, and 1H forecasts keep updating continuously and are not frozen by this tab.
        </p>
      </div>
      <div className="fbe-acc-tabs" role="tablist" aria-label="FBE session tabs">
        {SESSION_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={sessionTab === tab.key}
            className={`fbe-acc-tab ${sessionTab === tab.key ? "active" : ""}`}
            onClick={() => onSessionTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}
